/**
 * RegistrationErrorService - Centralized error handling for registration flows
 * 
 * Boundary: Does not perform validation logic - only displays errors and manages loading states
 * External dependencies: None - pure state management
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ErrorMessage {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info' | 'success';
  timestamp: Date;
  autoHide?: boolean;
  duration?: number;
}

/**
 * Manages error messages, loading states, and user feedback for registration forms.
 * 
 * Dependencies:
 * - BehaviorSubject: Real-time error and loading state updates
 * 
 * Side effects:
 * - BehaviorSubjects: errors$, loading$ (emit state changes)
 * - Timers: Auto-hide error messages after duration
 */
@Injectable({
  providedIn: 'root',
})
export class RegistrationErrorService {
  private errorsSubject = new BehaviorSubject<ErrorMessage[]>([]);
  public errors$ = this.errorsSubject.asObservable();

  private loadingStates = new Map<string, boolean>();
  private loadingSubject = new BehaviorSubject<Map<string, boolean>>(new Map());
  public loading$ = this.loadingSubject.asObservable();

  /**
   * Adds an error message to the display queue.
   * 
   * @param message - Error message text to display
   * @param type - Message severity level (default: 'error')
   * @param autoHide - Whether to auto-dismiss message (default: true)
   * @param duration - Auto-hide delay in milliseconds (default: 5000)
   * @returns void
   * - Observable semantics: Emits updated error array to errors$ subscribers
   * - HTTP errors: N/A
   * - State touched: Updates errorsSubject, schedules auto-removal timer
   * - Idempotency: NOT idempotent; each call adds new error message
   */
  addError(
    message: string,
    type: 'error' | 'warning' | 'info' | 'success' = 'error',
    autoHide = true,
    duration = 5000,
  ): void {
    const error: ErrorMessage = {
      id: this.generateId(),
      message,
      type,
      timestamp: new Date(),
      autoHide,
      duration,
    };

    const currentErrors = this.errorsSubject.value;
    this.errorsSubject.next([...currentErrors, error]);

    // Auto-hide if specified
    if (autoHide) {
      setTimeout(() => {
        this.removeError(error.id);
      }, duration);
    }
  }

  /**
   * Removes a specific error message by ID.
   * 
   * @param id - Unique error message identifier
   * @returns void
   * - Observable semantics: Emits updated error array to errors$ subscribers
   * - HTTP errors: N/A
   * - State touched: Updates errorsSubject
   * - Idempotency: Safe to call multiple times; no-op if ID not found
   */
  removeError(id: string): void {
    const currentErrors = this.errorsSubject.value;
    this.errorsSubject.next(currentErrors.filter((error) => error.id !== id));
  }

  /**
   * Clears all error messages from the queue.
   * 
   * @returns void
   * - Observable semantics: Emits empty array to errors$ subscribers
   * - HTTP errors: N/A
   * - State touched: Clears errorsSubject
   * - Idempotency: Safe to call multiple times
   */
  clearErrors(): void {
    this.errorsSubject.next([]);
  }

  /**
   * Sets loading state for a specific component or operation.
   * 
   * @param key - Unique identifier for the loading operation
   * @param loading - Loading status (true = loading, false = idle)
   * @returns void
   * - Observable semantics: Emits updated loading map to loading$ subscribers
   * - HTTP errors: N/A
   * - State touched: Updates loadingStates Map and loadingSubject
   * - Idempotency: Safe to call multiple times; replaces existing key value
   */
  setLoading(key: string, loading: boolean): void {
    this.loadingStates.set(key, loading);
    this.loadingSubject.next(new Map(this.loadingStates));
  }

  /**
   * Retrieves loading state for a specific operation.
   * 
   * @param key - Unique identifier for the loading operation
   * @returns True if loading, false if idle or key not found
   * - Observable semantics: N/A (synchronous read)
   * - HTTP errors: N/A
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  isLoading(key: string): boolean {
    return this.loadingStates.get(key) || false;
  }

  /**
   * Clears all loading states.
   * 
   * @returns void
   * - Observable semantics: Emits empty Map to loading$ subscribers
   * - HTTP errors: N/A
   * - State touched: Clears loadingStates and loadingSubject
   * - Idempotency: Safe to call multiple times
   */
  clearLoading(): void {
    this.loadingStates.clear();
    this.loadingSubject.next(new Map());
  }

  /**
   * Transforms API errors into user-friendly messages and displays them.
   * 
   * @param error - Error object from API call
   * @param context - Description of the operation that failed
   * @returns User-friendly error message string
   * - Observable semantics: Adds error to errors$ via addError()
   * - HTTP errors: Mapped to user-friendly messages (permission-denied, network-request-failed, etc.)
   * - State touched: Updates errorsSubject via addError()
   * - Idempotency: NOT idempotent; each call adds new error message
   */
  handleApiError(error: any, context: string): string {
    console.error(`Error in ${context}:`, error);

    let userMessage = 'An unexpected error occurred. Please try again.';

    if (error?.code) {
      switch (error.code) {
        case 'permission-denied':
          userMessage = 'You do not have permission to perform this action.';
          break;
        case 'network-request-failed':
          userMessage =
            'Network connection failed. Please check your internet connection.';
          break;
        case 'quota-exceeded':
          userMessage =
            'Service temporarily unavailable. Please try again later.';
          break;
        case 'invalid-argument':
          userMessage =
            'Invalid information provided. Please check your input.';
          break;
        default:
          userMessage = error.message || userMessage;
      }
    } else if (error?.message) {
      userMessage = error.message;
    }

    this.addError(`${context}: ${userMessage}`, 'error');
    return userMessage;
  }

  /**
   * Displays success notification.
   * 
   * @param message - Success message text
   * @param duration - Auto-hide delay in milliseconds (default: 3000)
   * @returns void
   * - Observable semantics: Emits to errors$ via addError() with 'success' type
   * - HTTP errors: N/A
   * - State touched: Updates errorsSubject
   * - Idempotency: NOT idempotent; each call adds new message
   */
  showSuccess(message: string, duration = 3000): void {
    this.addError(message, 'success', true, duration);
  }

  /**
   * Displays warning notification.
   * 
   * @param message - Warning message text
   * @param duration - Auto-hide delay in milliseconds (default: 5000)
   * @returns void
   * - Observable semantics: Emits to errors$ via addError() with 'warning' type
   * - HTTP errors: N/A
   * - State touched: Updates errorsSubject
   * - Idempotency: NOT idempotent; each call adds new message
   */
  showWarning(message: string, duration = 5000): void {
    this.addError(message, 'warning', true, duration);
  }

  /**
   * Displays informational notification.
   * 
   * @param message - Info message text
   * @param duration - Auto-hide delay in milliseconds (default: 4000)
   * @returns void
   * - Observable semantics: Emits to errors$ via addError() with 'info' type
   * - HTTP errors: N/A
   * - State touched: Updates errorsSubject
   * - Idempotency: NOT idempotent; each call adds new message
   */
  showInfo(message: string, duration = 4000): void {
    this.addError(message, 'info', true, duration);
  }

  /**
   * Validates form and displays field-specific errors.
   * 
   * @param form - Angular form to validate
   * @param fieldLabels - Map of field names to user-friendly labels (default: {})
   * @returns True if form valid, false if errors found
   * - Observable semantics: Emits to errors$ via addError() if validation fails
   * - HTTP errors: N/A
   * - State touched: Updates errorsSubject if invalid
   * - Idempotency: Safe to call multiple times; only adds error if invalid
   */
  validateFormFields(
    form: any,
    fieldLabels: Record<string, string> = {},
  ): boolean {
    if (form.valid) {
      return true;
    }

    const errors: string[] = [];

    Object.keys(form.controls).forEach((key) => {
      const control = form.controls[key];
      const label = fieldLabels[key] || key;

      if (control.invalid && (control.dirty || control.touched)) {
        if (control.errors?.['required']) {
          errors.push(`${label} is required`);
        } else if (control.errors?.['email']) {
          errors.push(`${label} must be a valid email address`);
        } else if (control.errors?.['minlength']) {
          errors.push(
            `${label} must be at least ${control.errors['minlength'].requiredLength} characters`,
          );
        } else if (control.errors?.['pattern']) {
          errors.push(`${label} format is invalid`);
        }
      }
    });

    if (errors.length > 0) {
      this.addError(
        `Please fix the following errors: ${errors.join(', ')}`,
        'warning',
        true,
        8000,
      );
      return false;
    }

    return true;
  }

  /**
   * Generate unique ID for error messages using cryptographically secure random
   * @returns Unique ID string
   */
  private generateId(): string {
    const array = new Uint8Array(8);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('').substring(0, 9);
  }
}
